// Mock Data for Work Request Module - Following FSD Specifications
import dayjs from "dayjs";

// Types
export interface WorkRequestData {
  id: string;
  workRequestNumber: string;
  workRequestTitle: string;
  description?: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  requestFor: "Construction Work" | "Service" | "Maintenance" | "Others";
  plannedStartDate: string;
  plannedEndDate: string;
  approvalRequired: boolean;
  status: "Draft" | "Pending Approval" | "Approved" | "Rejected" | "In Progress" | "Completed";
  workLines: WorkRequestLine[];
  totalLines: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
}

export interface WorkRequestLine {
  id: string;
  taskTitle: string;
  taskDescription?: string;
  category: string;
  subcategory?: string;
  quantity: number;
  uom: string;
  source: "Project Management" | "Work BOQ" | "New Work Line";
  sourceId?: string;
  assignedTower?: string;
  assignedVilla?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  documents?: string[];
}

export interface ProjectTask {
  taskId: string;
  taskName: string;
  description: string;
  quantity: number;
  uom: string;
  plannedStart: string;
  plannedEnd: string;
  activityId: string;
  activityName: string;
  projectId: string;
}

export interface WorkBoqLine {
  lineId: string;
  category: string;
  subcategory: string;
  taskName: string;
  quantity: number;
  uom: string;
  towerFlatInfo: string;
  documents: string[];
  workBoqId: string;
}

export interface MasterData {
  sites: { id: string; name: string; }[];
  projects: { id: string; name: string; siteId: string; }[];
  activities: { id: string; name: string; projectId: string; }[];
  workBoqs: { id: string; title: string; siteId: string; }[];
  categories: { id: string; name: string; parentId?: string; }[];
  uomList: { id: string; name: string; code: string; }[];
}

// Master Data
export const mockMasterData: MasterData = {
  sites: [
    { id: "site-1", name: "Site A - Downtown" },
    { id: "site-2", name: "Site B - Suburban" },
    { id: "site-3", name: "Site C - Industrial" }
  ],
  projects: [
    { id: "proj-1", name: "Highrise Tower Phase 1", siteId: "site-1" },
    { id: "proj-2", name: "Urban Complex A", siteId: "site-1" },
    { id: "proj-3", name: "Green Meadows Villa", siteId: "site-2" },
    { id: "proj-4", name: "Industrial Park B", siteId: "site-3" }
  ],
  activities: [
    { id: "act-1", name: "Foundation Work", projectId: "proj-1" },
    { id: "act-2", name: "Structure Work", projectId: "proj-1" },
    { id: "act-3", name: "Finishing Work", projectId: "proj-1" },
    { id: "act-4", name: "Landscaping", projectId: "proj-3" },
    { id: "act-5", name: "Infrastructure", projectId: "proj-4" }
  ],
  workBoqs: [
    { id: "boq-1", title: "Civil Works BOQ #001", siteId: "site-1" },
    { id: "boq-2", title: "Plumbing Works BOQ #002", siteId: "site-1" },
    { id: "boq-3", title: "Electrical Works BOQ #003", siteId: "site-2" }
  ],
  categories: [
    { id: "cat-1", name: "Civil" },
    { id: "cat-2", name: "Plumbing" },
    { id: "cat-3", name: "Electrical" },
    { id: "cat-4", name: "Masonry", parentId: "cat-1" },
    { id: "cat-5", name: "Excavation", parentId: "cat-1" },
    { id: "cat-6", name: "Internal Plumbing", parentId: "cat-2" },
    { id: "cat-7", name: "External Plumbing", parentId: "cat-2" }
  ],
  uomList: [
    { id: "uom-1", name: "Square Feet", code: "Sqft" },
    { id: "uom-2", name: "Meter", code: "Mtr" },
    { id: "uom-3", name: "Kilogram", code: "Kg" },
    { id: "uom-4", name: "Number", code: "Nos" },
    { id: "uom-5", name: "Cubic Meter", code: "Cum" }
  ]
};

// Project Tasks (FSD Table Format)
export const mockProjectTasks: ProjectTask[] = [
  {
    taskId: "T-001",
    taskName: "Excavation",
    description: "Ground Digging",
    quantity: 1000,
    uom: "Sqft",
    plannedStart: "01/15/2025",
    plannedEnd: "01/25/2025",
    activityId: "act-1",
    activityName: "Foundation Work",
    projectId: "proj-1"
  },
  {
    taskId: "T-002",
    taskName: "Foundation Laying",
    description: "Concrete foundation work",
    quantity: 500,
    uom: "Sqft",
    plannedStart: "01/26/2025",
    plannedEnd: "02/05/2025",
    activityId: "act-1",
    activityName: "Foundation Work",
    projectId: "proj-1"
  },
  {
    taskId: "T-003",
    taskName: "Column Construction",
    description: "RCC column construction",
    quantity: 20,
    uom: "Nos",
    plannedStart: "02/06/2025",
    plannedEnd: "02/20/2025",
    activityId: "act-2",
    activityName: "Structure Work",
    projectId: "proj-1"
  },
  {
    taskId: "T-004",
    taskName: "Beam Installation",
    description: "Pre-cast beam installation",
    quantity: 15,
    uom: "Nos",
    plannedStart: "02/21/2025",
    plannedEnd: "03/05/2025",
    activityId: "act-2",
    activityName: "Structure Work",
    projectId: "proj-1"
  }
];

// Work BOQ Lines (FSD Table Format)
export const mockWorkBoqLines: WorkBoqLine[] = [
  {
    lineId: "L-005",
    category: "Civil",
    subcategory: "Excavation",
    taskName: "Base Digging",
    quantity: 1500,
    uom: "Sqft",
    towerFlatInfo: "Tower A / G-01",
    documents: ["doc1.pdf"],
    workBoqId: "boq-1"
  },
  {
    lineId: "L-006",
    category: "Civil",
    subcategory: "Masonry",
    taskName: "Wall Construction",
    quantity: 200,
    uom: "Sqft",
    towerFlatInfo: "Tower A / Floor 1",
    documents: [],
    workBoqId: "boq-1"
  },
  {
    lineId: "L-007",
    category: "Plumbing",
    subcategory: "Internal Plumbing",
    taskName: "Pipe Installation",
    quantity: 150,
    uom: "Mtr",
    towerFlatInfo: "Tower B / All Floors",
    documents: ["pipe_spec.pdf"],
    workBoqId: "boq-2"
  },
  {
    lineId: "L-008",
    category: "Plumbing",
    subcategory: "External Plumbing",
    taskName: "Main Line Connection",
    quantity: 50,
    uom: "Mtr",
    towerFlatInfo: "Building Level",
    documents: [],
    workBoqId: "boq-2"
  }
];

// Sample Work Request Lines
const sampleWorkLines: WorkRequestLine[] = [
  {
    id: "line-1",
    taskTitle: "Wall Cladding Works",
    taskDescription: "External wall cladding installation",
    category: "Civil",
    subcategory: "Masonry",
    quantity: 250,
    uom: "Sqft",
    source: "New Work Line",
    assignedTower: "Tower A",
    plannedStartDate: "09/10/2025",
    plannedEndDate: "09/20/2025",
    documents: []
  },
  {
    id: "line-2",
    taskTitle: "Pipe Fitting Level 3",
    taskDescription: "Internal plumbing pipe installation for level 3",
    category: "Plumbing",
    subcategory: "Internal Plumbing",
    quantity: 100,
    uom: "Mtr",
    source: "Work BOQ",
    sourceId: "L-007",
    assignedTower: "Tower B",
    plannedStartDate: "09/21/2025",
    plannedEndDate: "09/27/2025",
    documents: []
  }
];

// Mock Work Requests (FSD Sample Data)
export const mockWorkRequests: WorkRequestData[] = [
  {
    id: "wr-1",
    workRequestNumber: "WR-000158",
    workRequestTitle: "Plumbing Setup -- Phase A",
    description: "Installation of piping works",
    siteId: "site-1",
    siteName: "Site A - Downtown",
    projectId: "proj-1",
    projectName: "Highrise Tower Phase 1",
    requestFor: "Construction Work",
    plannedStartDate: "09/01/2025",
    plannedEndDate: "11/15/2025",
    approvalRequired: true,
    status: "Pending Approval",
    workLines: sampleWorkLines,
    totalLines: 2,
    createdAt: dayjs().subtract(2, 'days').format('YYYY-MM-DD HH:mm:ss'),
    createdBy: "Site Engineer John",
    updatedAt: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm:ss')
  },
  {
    id: "wr-2",
    workRequestNumber: "WR-000159",
    workRequestTitle: "Basement Excavation",
    description: "Excavation work for basement construction",
    siteId: "site-1",
    siteName: "Site A - Downtown",
    projectId: "proj-1",
    projectName: "Highrise Tower Phase 1",
    requestFor: "Construction Work",
    plannedStartDate: "01/15/2025",
    plannedEndDate: "02/15/2025",
    approvalRequired: true,
    status: "Approved",
    workLines: [
      {
        id: "line-3",
        taskTitle: "Ground Excavation",
        category: "Civil",
        subcategory: "Excavation",
        quantity: 1000,
        uom: "Sqft",
        source: "Project Management",
        sourceId: "T-001"
      }
    ],
    totalLines: 1,
    createdAt: dayjs().subtract(5, 'days').format('YYYY-MM-DD HH:mm:ss'),
    createdBy: "Project Manager Rakesh"
  },
  {
    id: "wr-3",
    workRequestNumber: "WR-000160",
    workRequestTitle: "Drainage Repair",
    description: "Repair drainage system in building B",
    siteId: "site-2",
    siteName: "Site B - Suburban",
    projectId: "proj-3",
    projectName: "Green Meadows Villa",
    requestFor: "Maintenance",
    plannedStartDate: "01/20/2025",
    plannedEndDate: "01/25/2025",
    approvalRequired: false,
    status: "Draft",
    workLines: [],
    totalLines: 0,
    createdAt: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm:ss'),
    createdBy: "Site Supervisor Suma"
  }
];

// Helper Functions
export const getMasterData = () => mockMasterData;

export const getWorkRequests = (filters?: {
  searchText?: string;
  siteIds?: string[];
  statuses?: string[];
  requestFor?: string[];
}) => {
  let filtered = [...mockWorkRequests];
  
  if (filters?.searchText) {
    const search = filters.searchText.toLowerCase();
    filtered = filtered.filter(wr => 
      wr.workRequestNumber.toLowerCase().includes(search) ||
      wr.workRequestTitle.toLowerCase().includes(search) ||
      wr.createdBy.toLowerCase().includes(search)
    );
  }
  
  if (filters?.siteIds?.length) {
    filtered = filtered.filter(wr => filters.siteIds!.includes(wr.siteId));
  }
  
  if (filters?.statuses?.length) {
    filtered = filtered.filter(wr => filters.statuses!.includes(wr.status));
  }
  
  if (filters?.requestFor?.length) {
    filtered = filtered.filter(wr => filters.requestFor!.includes(wr.requestFor));
  }
  
  return filtered;
};

export const getWorkRequestById = (id: string) => {
  return mockWorkRequests.find(wr => wr.id === id);
};

export const getProjectTasks = (projectId?: string, activityId?: string) => {
  let tasks = [...mockProjectTasks];
  
  if (projectId) {
    tasks = tasks.filter(task => task.projectId === projectId);
  }
  
  if (activityId) {
    tasks = tasks.filter(task => task.activityId === activityId);
  }
  
  return tasks;
};

export const getWorkBoqLines = (workBoqId?: string, categoryId?: string) => {
  let lines = [...mockWorkBoqLines];
  
  if (workBoqId) {
    lines = lines.filter(line => line.workBoqId === workBoqId);
  }
  
  if (categoryId) {
    const category = mockMasterData.categories.find(c => c.id === categoryId);
    if (category) {
      lines = lines.filter(line => line.category === category.name);
    }
  }
  
  return lines;
};

export const createWorkRequest = (data: Partial<WorkRequestData>): WorkRequestData => {
  const newId = `wr-${Date.now()}`;
  const newWorkRequest: WorkRequestData = {
    id: newId,
    workRequestNumber: data.workRequestNumber || `WR-${String(Date.now()).slice(-6)}`,
    workRequestTitle: data.workRequestTitle || '',
    description: data.description,
    siteId: data.siteId || '',
    siteName: data.siteName || '',
    projectId: data.projectId,
    projectName: data.projectName,
    requestFor: data.requestFor || 'Construction Work',
    plannedStartDate: data.plannedStartDate || '',
    plannedEndDate: data.plannedEndDate || '',
    approvalRequired: data.approvalRequired || false,
    status: data.approvalRequired ? 'Pending Approval' : 'Draft',
    workLines: data.workLines || [],
    totalLines: data.workLines?.length || 0,
    createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    createdBy: data.createdBy || 'Current User'
  };
  
  mockWorkRequests.push(newWorkRequest);
  return newWorkRequest;
};

export const updateWorkRequest = (id: string, data: Partial<any>) => {
  const index = mockWorkRequests.findIndex(wr => wr.id === id);
  if (index !== -1) {
    mockWorkRequests[index] = {
      ...mockWorkRequests[index],
      ...data,
      updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss')
    };
    return mockWorkRequests[index];
  }
  return null;
};

export const deleteWorkRequest = (id: string) => {
  const index = mockWorkRequests.findIndex(wr => wr.id === id);
  if (index !== -1) {
    mockWorkRequests.splice(index, 1);
    return true;
  }
  return false;
};

export const generateWorkRequestNumber = () => {
  const timestamp = Date.now().toString();
  const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `WR-${timestamp.slice(-6)}`;
};